import {useContext, useEffect, useState} from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import {FiBattery, FiBluetooth, FiClock, FiRefreshCw, FiThermometer, FiX} from "react-icons/fi";
import {AdapterContext, ConnectionContext} from "../Contexts";

const emptyStatus = {
  battery: null,
  temperature: null,
  temperatureMode: 0,
  firmwareVersion: null,
};

const emptyClock = {syncedAt: null, error: null, timezone: "Local time"};

export function DeviceState() {
  const adapter = useContext(AdapterContext);
  const connected = useContext(ConnectionContext);
  const toast = useToast();
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState(emptyStatus);
  const [clock, setClock] = useState(emptyClock);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    if (connected) {
      setStatus(adapter.getStatus());
      setClock(adapter.getClockStatus?.() ?? emptyClock);
    } else {
      setStatus(emptyStatus);
      setClock(emptyClock);
    }
  }, [connected, adapter]);

  useEffect(() => {
    if (!connected) return undefined;
    const updateClock = () => {
      setNow(new Date());
      setClock(adapter.getClockStatus?.() ?? emptyClock);
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, [adapter, connected]);

  useEffect(() => adapter.onCommand(() => {
    if (!adapter.connected) return;
    setStatus(adapter.getStatus());
    setClock(adapter.getClockStatus?.() ?? emptyClock);
  }), [adapter]);

  const connect = async () => {
    if (!adapter || connecting) return;
    setConnecting(true);
    try {
      await adapter.connect();
      setStatus(adapter.getStatus());
      setClock(adapter.getClockStatus?.() ?? emptyClock);
    } catch (error) {
      toast({
        title: "Connection failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setConnecting(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await adapter.refreshStatus();
      setStatus(adapter.getStatus());
      setClock(adapter.getClockStatus?.() ?? emptyClock);
    } catch (error) {
      toast({title: "Refresh failed", description: error.message, status: "error", duration: 4000});
    } finally {
      setRefreshing(false);
    }
  };

  if (!connected) {
    const bluetoothAvailable = typeof navigator !== "undefined" && Boolean(navigator.bluetooth);
    return (
      <HStack spacing={{base: 2, md: 3}}>
        <HStack spacing={2} display={{base: "none", sm: "flex"}}>
          <Box width="6px" height="6px" bg="#62676f" borderRadius="full" />
          <Text color="#7f858d" fontSize="11px" fontWeight="700">No display</Text>
        </HStack>
        <Button
          leftIcon={connecting ? <Spinner size="xs" /> : <FiBluetooth />}
          colorScheme="studio"
          variant="solid"
          size="sm"
          color="#17130a"
          borderRadius="5px"
          onClick={connect}
          isDisabled={!adapter || connecting || !bluetoothAvailable}
        >
          {bluetoothAvailable ? "Connect" : "Unavailable"}
        </Button>
      </HStack>
    );
  }

  return (
    <Flex
      align="center"
      gap={{base: 2, md: 3}}
      wrap="wrap"
      justify={{base: "flex-start", md: "flex-end"}}
    >
      <Box minW={0} mr={1} display={{base: "none", sm: "block"}}>
        <HStack spacing={2}>
          <Box width="7px" height="7px" bg="#5ed5a8" borderRadius="full" boxShadow="0 0 8px rgba(94,213,168,.5)" />
          <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{adapter.deviceName}</Text>
        </HStack>
        {status.firmwareVersion && <Text fontSize="10px" color="gray.500">Firmware {status.firmwareVersion}</Text>}
      </Box>

      <Divider orientation="vertical" h="28px" borderColor="whiteAlpha.200" display={{base: "none", sm: "block"}} />

      <HStack spacing={0} color="#c9ccd0" border="1px solid" borderColor="#2c3035" borderRadius="5px" bg="#111315">
        <HStack px={{base: 2, md: 3}} minH="32px" spacing={1}>
          <FiBattery />
          <Text fontSize="xs" fontWeight="700">{status.battery == null ? "--" : `${status.battery}%`}</Text>
        </HStack>
        <Button
          size="sm"
          leftIcon={<FiThermometer />}
          variant="ghost"
          borderLeft="1px solid"
          borderColor="#2c3035"
          borderRadius="0"
          minW={0}
          px={{base: 2, md: 3}}
          fontSize="xs"
          onClick={async () => {
            const nextMode = status.temperatureMode ? 0 : 1;
            try {
              const next = await adapter.setTemperatureUnit(nextMode);
              setStatus(next);
            } catch (error) {
              toast({title: "Unit change failed", description: error.message, status: "error", duration: 4000});
            }
          }}
        >
          {status.temperature ?? "--"} °{status.temperatureMode ? "F" : "C"}
        </Button>
        <Tooltip
          label={clock.error ?? (clock.syncedAt ? `Synced on connect · ${clock.timezone}` : "Time sync pending")}
          hasArrow
        >
          <HStack px={{base: 2, md: 3}} minH="32px" spacing={1} borderLeft="1px solid" borderColor="#2c3035" color={clock.error ? "orange.300" : "#c9ccd0"}>
            <FiClock />
            <Text fontSize="xs" fontWeight="700">
              {new Intl.DateTimeFormat([], {hour: "2-digit", minute: "2-digit"}).format(now)}
            </Text>
          </HStack>
        </Tooltip>
      </HStack>

      <Tooltip label="Refresh device" hasArrow>
        <IconButton
          aria-label="Refresh device"
          icon={<FiRefreshCw />}
          size="sm"
          variant="ghost"
          borderRadius="5px"
          onClick={refresh}
          isLoading={refreshing}
        />
      </Tooltip>
      <Tooltip label="Disconnect" hasArrow>
        <IconButton
          aria-label="Disconnect"
          icon={<FiX />}
          size="sm"
          variant="ghost"
          color="gray.500"
          borderRadius="5px"
          onClick={() => adapter.disconnect()}
        />
      </Tooltip>
    </Flex>
  );
}
